import mergeWith from 'lodash.mergewith';
import omit from 'lodash.omit';
import React from 'react';

import { CARD_ELEMENT_TYPE } from '../../../services/cards/constants';
import { isCardElementEditable } from '../../../services/cards/components/AccountCard/helpers';

/**
 * @enum CONTROL_SECTION
 * @type {{ELEMENTS: string, LAYOUT: string, BACKGROUND: string}}
 */
export const CONTROL_SECTION = {
  LOGO: 'LOGO',
  LAYOUT: 'LAYOUT',
  ELEMENTS: 'ELEMENTS',
  PHOTOS: 'PHOTOS',
  TEXTS: 'TEXTS',
  LAYERS: 'LAYERS',
  BACKGROUND: 'BACKGROUND',
  FOREGROUND: 'FOREGROUND',
  BARCODE: 'BARCODE',
  GRID: 'GRID',
};

export const MAX_Z_INDEX = 1000;

/**
 * Get a card side.
 *
 * @param {Object} options The options.
 * @param {Object} options.card The Card.
 * @param {CARD_SIDE} options.side The CardSideEnum.
 * @param {String} options.cardSideId A Card Side Id
 * @returns {null|*}
 */
export const getCardSide = ({ card, side, cardSideId }) => {
  if (!card || !side) {
    return null;
  }

  const { records: cardSideGroups = [] } = card.card_side_groups ?? {};

  const cardSideGroup = cardSideGroups.find(x => x.side === side);
  const [cardSide] = cardSideGroup.card_sides.records ?? [];

  if (cardSideId) {
    const cardSideCardSideGroup = cardSideGroups.find(
      x =>
        x.side === side &&
        !!(x.card_sides?.records ?? []).find(({ id }) => id === cardSideId),
    );

    if (cardSideCardSideGroup) {
      return cardSideCardSideGroup.card_sides.records.find(
        ({ id }) => id === cardSideId,
      );
    }
  }

  return cardSide;
};

/**
 * Creates the CardSide UI state.
 *
 * @param {Object} cardSide The CardSide.
 * @returns {Array}
 */
export const getCardSideElementsConfiguration = cardSide => {
  const cardElementConfigurations = cardSide.card_element_configurations;

  return cardSide.card_template.card_elements.map(cardElement => {
    const cardElementConfiguration = cardElementConfigurations.find(
      x => x.card_element.id === cardElement.id,
    );

    return {
      cardElement,
      cardElementConfiguration: mergeWith(
        // TODO [cards] add default static card element configuration
        {},
        cardElementConfiguration,
      ),
      cardSide,
      side: cardSide.card_template.side,
    };
  });
};

/**
 * Returns CardElementConfigurationInput by element type.
 * Designer version.
 *
 * @param {Object} element The element state.
 * @param {Object} config The configuration.
 * @param {Boolean} [editable] If the CardElement is or will be editable.
 * @returns {{sticker_element_configuration: *}|{}|{photo_element_configuration: *}|{rectangle_element_configuration: *}|{text_element_configuration: *}}
 */
export const getCardDesignerCardElementConfigurationInputByElement = (
  element,
  config,
  editable,
) => {
  const { cardElement, cardElementConfiguration } = element;
  const isEditable = (() => {
    if (editable != null) {
      return editable;
    }

    return isCardElementEditable(element);
  })();

  switch (cardElement.type) {
    case CARD_ELEMENT_TYPE.TEXT:
      return {
        // 1. When the element is editable we want the text entered
        // in the designer to be a help placeholder.
        help_placeholder: isEditable
          ? config.text ||
            config.help_placeholder ||
            cardElementConfiguration?.text ||
            cardElementConfiguration?.help_placeholder
          : null,
        text_element_configuration: {
          font_family: cardElementConfiguration?.font_family,
          font_color: cardElementConfiguration?.font_color,
          text_align: cardElementConfiguration?.text_align,
          font_size: cardElementConfiguration?.font_size,
          font_weight: cardElementConfiguration?.font_weight,
          ...omit(config, ['help_placeholder']),
          // 2. And when the text element is not editable, only then do we want
          // the text entered to be a config (i.e. printed).
          text: !isEditable
            ? config.text ||
              cardElementConfiguration?.help_placeholder ||
              cardElementConfiguration?.text
            : null,
        },
      };
    case CARD_ELEMENT_TYPE.PHOTO:
      return {
        photo_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.STICKER:
      return {
        sticker_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.RECTANGLE:
      return {
        rectangle_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.ADDRESS:
      return {
        address_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.BARCODE:
      return {
        barcode_element_configuration: config,
      };
    default:
      return {};
  }
};

/**
 * Given a printSize, width and height, determine the print size title
 *
 * @param {Object} opts the options
 * @param {Object} opts.printSize the print size
 * @param {Number} opts.width the width
 * @param {height} opts.height the height
 *
 * @returns {JSX.Element|string}
 */
export const getPrintSizeTitle = ({ printSize, width, height }) => {
  // Resolves for 8x10 and 10x8, since values of width x height
  // will be determined by orientation.
  if (
    `${width}x${height}` === printSize.name ||
    `${height}x${width}` === printSize.name
  ) {
    return `${width}x${height}`;
  }

  return (
    <>
      {(printSize.name ?? '').replace('Template', '').trim()}
      <br />({width}x{height})
    </>
  );
};

/**
 * Given a printProduct & printSize, retrieve the matching printProductSize
 *
 * @param {Object} opts the options
 * @param {Object} opts.printProduct the base print product
 * @param {Object} opts.printSize the print size to search for
 * @param {Boolean} opts.useDefault wether or not to return the default printProductSize (8x10)
 *
 * @returns {null|*}
 */
export const getPrintProductSizeFromPrintSize = ({
  printProduct,
  printSize,
  useDefault = false,
}) => {
  if (!printProduct) {
    return null;
  }

  const defaultPrintProductSize = printProduct.sizes.records.find(
    ({ print_size: size }) => size.name === '8x10',
  );

  if (!printSize && useDefault) {
    return defaultPrintProductSize;
  }

  const printProductSize = printProduct.sizes.records.find(
    ({ print_size: size }) => size.id === printSize.id,
  );

  if (printProductSize) {
    return printProductSize;
  }

  if (useDefault) {
    return defaultPrintProductSize;
  }

  return null;
};
